import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * APEX Design System — Modal
 *
 * Modal de base réutilisable. Gère overlay + esc + scroll lock.
 *
 * Props:
 *   - open     : booléen (contrôlé)
 *   - onClose  : callback de fermeture
 *   - title    : titre (optionnel)
 *   - subtitle : sous-titre / éyebrow (optionnel)
 *   - size     : sm | md | lg | xl
 *   - children : contenu
 *   - footer   : actions du modal (optionnel)
 *   - hideClose: cacher le bouton X (modal critique)
 *
 * Usage:
 *   <Modal
 *     open={showModal}
 *     onClose={() => setShowModal(false)}
 *     title="Comment c'était ?"
 *     subtitle="Ressenti"
 *     size="md"
 *     footer={<Button variant="primary">Appliquer</Button>}
 *   >
 *     <p>Contenu...</p>
 *   </Modal>
 */

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl'
};

export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  children,
  footer,
  hideClose = false
}) {
  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => {
      if (e.key === 'Escape' && !hideClose) onClose?.();
    };
    document.addEventListener('keydown', onEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [open, onClose, hideClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-2 md:p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget && !hideClose) onClose?.();
      }}
    >
      <div
        className={`bg-zinc-950 border border-cyan-400/30 w-full ${SIZES[size]} my-2 md:my-8`}
        style={{ boxShadow: '0 0 40px rgba(34, 211, 238, 0.15)' }}
      >
        {(title || subtitle) && (
          <div className="border-b border-zinc-800 p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-1 h-7 bg-gradient-to-b from-cyan-400 to-fuchsia-400 shrink-0" />
              <div className="min-w-0">
                {subtitle && (
                  <div className="text-[10px] tracking-[0.3em] text-cyan-400 uppercase font-semibold">
                    {subtitle}
                  </div>
                )}
                {title && (
                  <div className="font-black text-base text-white truncate">{title}</div>
                )}
              </div>
            </div>
            {!hideClose && (
              <button
                onClick={onClose}
                className="text-zinc-500 hover:text-white transition shrink-0"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}

        <div className="p-5">
          {children}
        </div>

        {footer && (
          <div className="border-t border-zinc-800 p-3 flex gap-2 justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
